// color.js — the color() animation cue
// Animates SVG fill/stroke colors using HSL interpolation
//
// Supports:
// - Discrete color sequences: vals:[#f00, #0f0, #00f]
// - Pattern sequences: vals:Pseq([#f00, #ff0], 3)
// - Continuous hue cycling: vals:hue or vals:hue(120, 240)
// - Modes: linear (smooth), alt (ping-pong), step (hard switch)

import { isOff } from "../parser/cueUtils.js";
import { registerAnimation, autoStartAnimation } from "./animation.js";
import { scheduleCueStart } from "./cueDispatcher.js";
import {
    applyPrestateBeforeStart,
    applyPrestateOnStart,
    installOscToggleHandler,
    installToggleStatesHandler,
    armElement,
    needsArming,
    needsFadeIn,
    triggerFadeIn,
    isOscEnabled
} from "./animShared.js";

import { createOscOverlay } from "./osc.js";
import { makeValueGenerator, evalMaybeRandom, isRandomised } from "./audio/audioShared.js";
import { sendOSC } from "../system/oscillaOSCClient.js";
import { publish } from '../control/paramBinding.js';
import { setupSpeedBinding, isSignalRef } from './cueParamBinding.js';
import { createAnimationLoop } from './cueAnimLoop.js';
import { secs } from './beats.js';

// ============================================================
// COLOR UTILITIES
// ============================================================

/**
 * Parse a color string to HSL
 * Supports: #rgb, #rrggbb, rgb(), hsl(), named colors
 */
// Any CSS color name → normalized #rrggbb via a canvas context (cached).
// Returns null for strings the browser doesn't recognize as a color.
let _colorNormCtx = null;
const _cssColorCache = new Map();
function cssColorToHex(name) {
    if (_cssColorCache.has(name)) return _cssColorCache.get(name);
    let hex = null;
    try {
        if (!_colorNormCtx) _colorNormCtx = document.createElement("canvas").getContext("2d");
        const SENTINEL = "#010203";
        _colorNormCtx.fillStyle = SENTINEL;
        _colorNormCtx.fillStyle = name;
        const v = String(_colorNormCtx.fillStyle);
        if (/^#[0-9a-f]{6}$/i.test(v) && (v !== SENTINEL || name.replace(/\s/g, "") === SENTINEL)) {
            hex = v;
        }
    } catch { /* no DOM / detached — fall through */ }
    _cssColorCache.set(name, hex);
    return hex;
}

function parseColorToHSL(colorStr) {
    if (!colorStr) return { h: 0, s: 50, l: 50 };

    // DSL rgb(…)/hsl(…) arrive from the parser as funcCall objects
    if (typeof colorStr === "object" && colorStr.type === "funcCall" &&
        (colorStr.name === "rgb" || colorStr.name === "hsl")) {
        colorStr = `${colorStr.name}(${(colorStr.args || []).join(",")})`;
    }

    const str = String(colorStr).trim().toLowerCase();
    
    // HSL format: hsl(h, s%, l%)
    const hslMatch = str.match(/^hsl\s*\(\s*([\d.]+)\s*,\s*([\d.]+)%?\s*,\s*([\d.]+)%?\s*\)$/i);
    if (hslMatch) {
        return {
            h: parseFloat(hslMatch[1]) % 360,
            s: parseFloat(hslMatch[2]),
            l: parseFloat(hslMatch[3])
        };
    }
    
    // RGB format: rgb(r, g, b)
    const rgbMatch = str.match(/^rgb\s*\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)$/i);
    if (rgbMatch) {
        return rgbToHSL(
            parseFloat(rgbMatch[1]),
            parseFloat(rgbMatch[2]),
            parseFloat(rgbMatch[3])
        );
    }
    
    // Hex format: #rgb or #rrggbb
    if (str.startsWith('#')) {
        const hex = str.slice(1);
        let r, g, b;
        
        if (hex.length === 3) {
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
        } else if (hex.length === 6) {
            r = parseInt(hex.slice(0, 2), 16);
            g = parseInt(hex.slice(2, 4), 16);
            b = parseInt(hex.slice(4, 6), 16);
        } else {
            return { h: 0, s: 50, l: 50 };
        }
        
        return rgbToHSL(r, g, b);
    }
    
    // Named colors (common subset)
    // none / transparent: no colour at all (a: 0). A glide to it fades the
    // colour before out; a glide from it fades the next one in.
    if (str === "none" || str === "transparent") return { h: 0, s: 0, l: 50, a: 0 };

    const namedColors = {
        red: { h: 0, s: 100, l: 50 },
        orange: { h: 30, s: 100, l: 50 },
        yellow: { h: 60, s: 100, l: 50 },
        green: { h: 120, s: 100, l: 50 },
        cyan: { h: 180, s: 100, l: 50 },
        blue: { h: 240, s: 100, l: 50 },
        purple: { h: 270, s: 100, l: 50 },
        magenta: { h: 300, s: 100, l: 50 },
        pink: { h: 330, s: 100, l: 75 },
        white: { h: 0, s: 0, l: 100 },
        black: { h: 0, s: 0, l: 0 },
        gray: { h: 0, s: 0, l: 50 },
        grey: { h: 0, s: 0, l: 50 }
    };
    
    if (namedColors[str]) {
        return { ...namedColors[str] };
    }

    // Any other CSS color name (gold, tomato, rebeccapurple, …) —
    // normalize to hex via the browser and reparse
    const normalized = cssColorToHex(str);
    if (normalized) return parseColorToHSL(normalized);

    // Fallback
    return { h: 0, s: 50, l: 50 };
}

/**
 * Convert RGB to HSL
 */
function rgbToHSL(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s;
    const l = (max + min) / 2;
    
    if (max === min) {
        h = s = 0;
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
        }
    }
    
    return {
        h: h * 360,
        s: s * 100,
        l: l * 100
    };
}

/**
 * Convert HSL to CSS string
 */
function hslToCSS(h, s, l, a = 1) {
    if (a <= 0) return "none";
    if (a < 1) return `hsla(${h.toFixed(1)}, ${s.toFixed(1)}%, ${l.toFixed(1)}%, ${a.toFixed(3)})`;
    return `hsl(${h.toFixed(1)}, ${s.toFixed(1)}%, ${l.toFixed(1)}%)`;
}

/**
 * Interpolate between two HSL colors
 * Uses shortest path for hue interpolation
 */
function interpolateHSL(from, to, t) {
    // to or from none: the other colour, fading
    const fa = from.a ?? 1, ta = to.a ?? 1;
    if (fa === 0 || ta === 0) {
        const c = fa === 0 ? to : from;
        return { h: c.h, s: c.s, l: c.l, a: fa + (ta - fa) * t };
    }
    // Hue interpolation (shortest path around the circle)
    let hDiff = to.h - from.h;
    if (hDiff > 180) hDiff -= 360;
    if (hDiff < -180) hDiff += 360;
    
    const h = (from.h + hDiff * t + 360) % 360;
    const s = from.s + (to.s - from.s) * t;
    const l = from.l + (to.l - from.l) * t;
    
    return { h, s, l };
}

/**
 * What carries the colour: a shape() is a group around one path whose own
 * fill and stroke would win over the group's, so its colour goes on the path.
 */
function paintTarget(el) {
    return el?.classList?.contains("oscilla-shape") ? (el.querySelector("path") ?? el) : el;
}

/**
 * Get current color from element
 */
function getCurrentColor(el, target = "both") {
    el = paintTarget(el);
    const style = window.getComputedStyle(el);
    
    // Try fill first, then stroke
    if (target === "fill" || target === "both") {
        const fill = style.fill;
        if (fill && fill !== 'none') {
            return parseColorToHSL(fill);
        }
    }
    
    if (target === "stroke" || target === "both") {
        const stroke = style.stroke;
        if (stroke && stroke !== 'none') {
            return parseColorToHSL(stroke);
        }
    }
    
    return { h: 0, s: 100, l: 50 };
}

/**
 * Apply color to element
 * @param {SVGElement} el - Target element
 * @param {object} hsl - HSL color object
 * @param {string} target - "fill", "stroke", or "both" (default)
 */
function applyColor(el, hsl, target = "both") {
    el = paintTarget(el);
    const css = hslToCSS(hsl.h, hsl.s, hsl.l, hsl.a);
    
    if (target === "fill" || target === "both") {
        el.style.fill = css;
    }
    
    if (target === "stroke" || target === "both") {
        el.style.stroke = css;
    }
}

// ============================================================
// OSC SEND HELPER
// ============================================================
function sendOSCColor(cfg, hsl) {
    const addr = cfg.oscAddr ?? cfg.oscaddr ?? cfg.addr ?? null;
    
    const payload = {
        type: "osc_color",
        uid: cfg.uid,
        h: hsl.h,
        s: hsl.s,
        l: hsl.l,
        hNorm: hsl.h / 360,
        sNorm: hsl.s / 100,
        lNorm: hsl.l / 100,
        timestamp: Date.now()
    };
    
    if (addr) payload.addr = addr;
    
    sendOSC(payload);
}

// ============================================================
// HUE CYCLING ENGINE
// Uses shared animation loop helper for dynamic speed control
// ============================================================
function handleColorHueCycle(el, cfg) {
    const astArgs = cfg.astArgs || [];

    let durValue = 6;
    let durBinding = null;
    let loop = 0;
    let hueStart = 0;
    let hueEnd = 360;
    let oscMode = 0;
    let target = cfg.target || "both";

    for (const arg of astArgs) {
        const key = arg.key || arg.type;
        const val = arg.value;

        if (key === "dur") {
            const d = secs(val);                // beats
            if (isSignalRef(d)) {
                durBinding = d;
                durValue = d.range?.[0] ?? 6;
            } else {
                durValue = Number(d) || 6;
            }
            cfg.dur = durValue;
        }
        if (key === "loop") loop = Number(val);
        if (key === "osc") oscMode = Number(val) || 0;
        if (key === "oscaddr" || key === "oscAddr") cfg.oscAddr = String(val).trim();
        if (key === "target") target = String(evalMaybeRandom(val)).toLowerCase();
    }

    // Check for hue range in vals: hue(start, end)
    // values: is accepted as a synonym — scale() and rotate() spell it that
    // way.
    const valsArg = astArgs.find(a => ["vals", "values"].includes(a.key) ||
                                      ["vals", "values"].includes(a.type));
    if (valsArg?.value?.type === "func" && valsArg.value.name === "hue") {
        const args = valsArg.value.args || [];
        if (args.length >= 2) {
            hueStart = Number(args[0]) || 0;
            hueEnd = Number(args[1]) || 360;
        }
    }

    // Set up speed binding
    const speedBinding = setupSpeedBinding(cfg, durBinding || durValue, {
        kind: 'color',
        baseDur: durValue,
        minDur: 0.5,
        maxDur: 120
    });

    // Get base saturation/lightness from element
    const baseColor = getCurrentColor(el, target);
    const baseSat = baseColor.s;
    const baseLight = baseColor.l;

    if (el._oscillaColorAnim?.stop) el._oscillaColorAnim.stop();

    const hueRange = hueEnd - hueStart;

    // OSC overlay
    if (cfg._overlay) {
        cfg._overlay.destroy();
        cfg._overlay = null;
    }

    if (isOscEnabled(cfg, oscMode)) {
        cfg._overlay = createOscOverlay({
            anchorEl: el,
            label: cfg.oscAddr || cfg.uid || "color",
            anchorMode: "center"
        });
        cfg._overlay.update("hue:0");
        cfg._overlay.position();
    }

    // Create animation loop using shared helper
    const animLoop = createAnimationLoop({
        dur: durValue,
        loop,
        getSpeed: () => cfg.speedMultiplier ?? 1,

        onTick: (phase) => {
            const currentHue = (hueStart + hueRange * phase) % 360;
            const hsl = { h: currentHue, s: baseSat, l: baseLight };

            applyColor(el, hsl, target);

            publish("color", cfg.uid, {
                hNorm: currentHue / 360,
                sNorm: baseSat / 100,
                lNorm: baseLight / 100
            });

            if (isOscEnabled(cfg, oscMode)) {
                sendOSCColor(cfg, hsl);
                if (cfg._overlay) {
                    cfg._overlay.update(`hue:${currentHue.toFixed(0)}`);
                }
            }

            cfg._currentT = phase;
        },

        onComplete: () => {
            const finalHue = hueEnd % 360;
            applyColor(el, { h: finalHue, s: baseSat, l: baseLight }, target);
            speedBinding.unbind();
        }
    });

    el._oscillaColorAnim = animLoop;
    cfg._anim = animLoop;

    animLoop.start();
}

// ============================================================
// COLOR SEQUENCE ENGINE
// Uses shared animation loop helper for dynamic speed control
// ============================================================
function handleColorSequence(el, cfg) {
    const astArgs = cfg.astArgs || [];

    let values = cfg.values || null;
    let pattern = cfg.pattern || null;
    let durValue = 1;
    let durBinding = null;
    let durGen = null;
    let mode = "linear"; // linear | alt | step
    let loop = 0;
    let oscMode = 0;
    let target = cfg.target || "both";

    let valueGen = null;

    for (const arg of astArgs) {
        const key = arg.key || arg.type;
        const val = arg.value;

        switch (key) {
            case "vals":
            case "values":
                if (val && val.type === "pattern") {
                    pattern = val;
                } else if (Array.isArray(val)) {
                    values = val.slice();
                }
                break;

            case "dur": {
                const d = secs(val);            // beats
                if (d && d.type === "pattern") {
                    durGen = makeValueGenerator(d);
                } else if (Array.isArray(d)) {
                    durGen = makeValueGenerator({ values: d });
                } else if (isSignalRef(d)) {
                    durBinding = d;
                    durValue = d.range?.[0] ?? 1;
                } else {
                    durValue = Number(d) || 1;
                }
                cfg.dur = durValue;
                break;
            }

            case "mode":
                mode = String(val).trim().toLowerCase();
                break;

            case "loop":
                loop = Number(val);
                break;

            case "osc":
                oscMode = Number(val) || 0;
                break;

            case "oscaddr":
            case "oscAddr":
                cfg.oscAddr = String(val).trim();
                break;

            case "target":
                target = String(evalMaybeRandom(val)).toLowerCase();
                break;
        }
    }

    // Set up speed binding
    const speedBinding = setupSpeedBinding(cfg, durBinding || durValue, {
        kind: 'color',
        baseDur: durValue,
        minDur: 0.1,
        maxDur: 30
    });

    if (pattern) {
        valueGen = makeValueGenerator(pattern);
        values = null;
    }

    if (!values && !valueGen) {
        console.warn("[color] No values for sequence");
        return;
    }

    let hslValues = [];
    if (values) {
        hslValues = values.map(v => parseColorToHSL(v));
    }

    if (el._oscillaColorAnim?.stop) el._oscillaColorAnim.stop();

    // OSC overlay
    if (cfg._overlay) {
        cfg._overlay.destroy();
        cfg._overlay = null;
    }

    if (isOscEnabled(cfg, oscMode)) {
        cfg._overlay = createOscOverlay({
            anchorEl: el,
            label: cfg.oscAddr || cfg.uid || "color",
            anchorMode: "center"
        });
        cfg._overlay.update("...");
        cfg._overlay.position();
    }

    // Animation state
    let currentIndex = 0;
    let direction = 1;
    let completedCycles = 0;

    // Initialize pattern colors
    if (valueGen) {
        el._colorPatternCurrent = parseColorToHSL(valueGen.next());
        el._colorPatternNext = parseColorToHSL(valueGen.next());
    }

    // Duration pattern steps: evaluate rand() funcCalls, fall back on junk
    const nextDur = () => {
        const d = Number(evalMaybeRandom(durGen?.next()));
        return Number.isFinite(d) && d > 0 ? d : durValue;
    };

    // Create animation loop using shared helper
    const animLoop = createAnimationLoop({
        dur: durGen ? nextDur() : durValue,
        loop: 0, // We handle looping ourselves for sequences
        getSpeed: () => cfg.speedMultiplier ?? 1,

        onTick: (phase) => {
            let fromHSL, toHSL;

            if (valueGen) {
                fromHSL = el._colorPatternCurrent;
                toHSL = el._colorPatternNext;
            } else {
                const nextIndex = mode === "alt"
                    ? currentIndex + direction
                    : (currentIndex + 1) % hslValues.length;

                fromHSL = hslValues[currentIndex];
                toHSL = hslValues[Math.abs(nextIndex) % hslValues.length];
            }

            let currentHSL;
            if (mode === "step") {
                currentHSL = phase < 0.5 ? fromHSL : toHSL;
            } else {
                currentHSL = interpolateHSL(fromHSL, toHSL, phase);
            }

            applyColor(el, currentHSL, target);

            publish("color", cfg.uid, {
                hNorm: currentHSL.h / 360,
                sNorm: currentHSL.s / 100,
                lNorm: currentHSL.l / 100
            });

            if (isOscEnabled(cfg, oscMode)) {
                sendOSCColor(cfg, currentHSL);
                if (cfg._overlay) {
                    cfg._overlay.update(`h:${currentHSL.h.toFixed(0)}`);
                }
            }

            cfg._currentT = phase;
        },

        onLoopComplete: () => {
            if (durGen) animLoop.setDur(nextDur());
            if (valueGen) {
                el._colorPatternCurrent = el._colorPatternNext;
                const nextVal = valueGen.next();
                if (nextVal === null) {
                    animLoop.stop();
                    speedBinding.unbind();
                    return;
                }
                el._colorPatternNext = parseColorToHSL(nextVal);
            } else {
                if (mode === "alt") {
                    currentIndex += direction;
                    if (currentIndex >= hslValues.length - 1) {
                        direction = -1;
                        completedCycles++;
                    } else if (currentIndex <= 0) {
                        direction = 1;
                        completedCycles++;
                    }
                } else {
                    currentIndex = (currentIndex + 1) % hslValues.length;
                    if (currentIndex === 0) {
                        completedCycles++;
                    }
                }

                if (loop > 0 && completedCycles >= loop) {
                    animLoop.stop();
                    speedBinding.unbind();
                    return;
                }
            }
            // the loop runs on into the next segment, with what the frame ran
            // past the wrap already counted
        },

        onComplete: () => {
            speedBinding.unbind();
        }
    });

    el._oscillaColorAnim = animLoop;
    cfg._anim = animLoop;

    animLoop.start();
}

// Register click handler for trig:click — re-registers safely on playhead re-arm
function armForClick(el, startFn) {
    if (el._colorClickHandler) {
        el.removeEventListener("click", el._colorClickHandler);
    }
    if (el.dataset) el.dataset.cueClickable = "1";
    el.style.cursor = "pointer";
    const handler = () => {
        el.removeEventListener("click", handler);
        el._colorClickHandler = null;
        el.style.cursor = "";
        startFn();
    };
    el._colorClickHandler = handler;
    el.addEventListener("click", handler);
}

// ============================================================
// MAIN CUE HANDLER
// ============================================================
export function handleColorCue(el, astArgs, options = {}) {
    if (!el) return;
    
    const { fromCueTrigger = false } = options;
    
    // Check for existing cfg (playhead re-trigger)
    const existingCfg = el._oscillaCfg;
    
    if (fromCueTrigger && existingCfg && existingCfg._armed) {
        if (needsArming(existingCfg)) {
            armElement(el, existingCfg);
        }
        return;
    }
    
    if (fromCueTrigger && existingCfg && existingCfg._fadeIn) {
        if (needsFadeIn(existingCfg)) {
            triggerFadeIn(el, existingCfg);
        }
        return;
    }
    
    // Parse basic params
    let trig = "auto";
    let uid = el.id || ("color_" + Math.random().toString(36).slice(2));
    let cfgStartDelay = 0;
    let init = "show";
    let target = "both"; // fill, stroke, or both
    let sync = true;
    let syncExplicit = false;

    for (const a of astArgs) {
        const key = a.key || a.type;
        const val = a.value;
        
        if (key === "sync") { syncExplicit = true; sync = !isOff(val); }
        if (key === "trig") trig = String(val).toLowerCase();
        if (key === "uid") uid = String(val).trim();
        if (key === "tdelay") cfgStartDelay = Number(secs(val)) || 0;   // beats
        if (key === "init" && val != null) init = val;
        if (key === "target") target = String(evalMaybeRandom(val)).toLowerCase();
    }
    // Random patterns / rand() draw per client by design: no drift correction
    // unless sync:1 is authored (see isRandomised).
    if (!syncExplicit && astArgs.some(a => isRandomised(a.value))) sync = false;
    
    // trig:playhead — fire only when dispatcher calls with fromCueTrigger=true
    // trig:auto    — fire immediately on load (animationAssign)
    // trig:click   — arm a click handler, never autostart
    const shouldStartNow = fromCueTrigger || trig === "auto";
    
    // Build config
    const baseCfg = {
        uid,
        trig,
        start: cfgStartDelay,
        init,
        target,
        astArgs,
        fromCueTrigger,
        kind: "color",
        sync,
        _anim: null
    };
    
    // Check vals argument to determine mode (values: is a synonym — see above)
    const valsArg = astArgs.find(a => ["vals", "values"].includes(a.key) ||
                                      ["vals", "values"].includes(a.type));
    
    // Determine if hue cycling mode
    let isHueCycle = false;
    if (valsArg?.value === "hue") {
        isHueCycle = true;
    } else if (valsArg?.value?.type === "func" && valsArg.value.name === "hue") {
        isHueCycle = true;
    }
    
    function makeRawStart(cfg, modeFn) {
        return () => {
            modeFn(el, cfg);
        };
    }
    
    function wrapStart(cfg, rawStartFn) {
        cfg._start = rawStartFn;
        cfg._applyPrestateOnStart = () => applyPrestateOnStart(el, cfg);
        
        return () => {
            if (cfg.start > 0) {
                scheduleCueStart(
                    cfg,
                    el,
                    () => {
                        if (cfg._armed && cfg._startBlocked) {
                            applyPrestateOnStart(el, cfg);
                            return;
                        }
                        applyPrestateOnStart(el, cfg);
                        rawStartFn();
                    },
                    cfg.uid
                );
            } else {
                if (cfg._armed && cfg._startBlocked) {
                    applyPrestateOnStart(el, cfg);
                    return;
                }
                applyPrestateOnStart(el, cfg);
                rawStartFn();
            }
        };
    }
    
    // ============================================================
    // HUE CYCLING MODE
    // ============================================================
    if (isHueCycle) {
        const cfg = {
            ...baseCfg,
            mode: "hue-cycle"
        };
        
        el._oscillaCfg = cfg;
        
        installOscToggleHandler(el, cfg);
        installToggleStatesHandler(el, cfg);
        applyPrestateBeforeStart(el, cfg);
        
        const rawStart = makeRawStart(cfg, handleColorHueCycle);
        const start = wrapStart(cfg, rawStart);
        
        registerAnimation(el, "color-hue", cfg, start);
        
        if (trig === "click") { armForClick(el, start); } else if (shouldStartNow) { autoStartAnimation(cfg.uid, start, fromCueTrigger); }
        return;
    }
    
    // ============================================================
    // PATTERN SEQUENCE MODE
    // ============================================================
    if (valsArg?.value?.type === "pattern") {
        const cfg = {
            ...baseCfg,
            pattern: valsArg.value,
            mode: "sequence-pattern"
        };
        
        el._oscillaCfg = cfg;
        
        installOscToggleHandler(el, cfg);
        installToggleStatesHandler(el, cfg);
        applyPrestateBeforeStart(el, cfg);
        
        // Apply first color
        if (Array.isArray(valsArg.value.values) && valsArg.value.values.length > 0) {
            const firstHSL = parseColorToHSL(valsArg.value.values[0]);
            applyColor(el, firstHSL, cfg.target);
        }
        
        const rawStart = makeRawStart(cfg, handleColorSequence);
        const start = wrapStart(cfg, rawStart);
        
        registerAnimation(el, "color-sequence", cfg, start);
        
        if (trig === "click") { armForClick(el, start); } else if (shouldStartNow) { autoStartAnimation(cfg.uid, start, fromCueTrigger); }
        return;
    }
    
    // ============================================================
    // LITERAL ARRAY SEQUENCE MODE
    // ============================================================
    if (valsArg && Array.isArray(valsArg.value)) {
        const cfg = {
            ...baseCfg,
            values: valsArg.value,
            mode: "sequence"
        };
        
        el._oscillaCfg = cfg;
        
        installOscToggleHandler(el, cfg);
        installToggleStatesHandler(el, cfg);
        applyPrestateBeforeStart(el, cfg);
        
        // Apply first color
        if (cfg.values.length > 0) {
            const firstHSL = parseColorToHSL(cfg.values[0]);
            applyColor(el, firstHSL, cfg.target);
        }
        
        const rawStart = makeRawStart(cfg, handleColorSequence);
        const start = wrapStart(cfg, rawStart);
        
        registerAnimation(el, "color-sequence", cfg, start);
        
        if (trig === "click") { armForClick(el, start); } else if (shouldStartNow) { autoStartAnimation(cfg.uid, start, fromCueTrigger); }
        return;
    }
    
    // ============================================================
    // FALLBACK: No vals specified - do nothing useful
    // ============================================================
    console.warn("[color] no vals:/values: found — nothing to animate.", astArgs);
}
